import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { CvPipelineDialogComponent } from '../cv-pipeline-dialog/cv-pipeline-dialog.component';
import {
  AggregationStatus,
  FinalizationStatus,
  Game,
  GameStatus
} from '../domain/game';
import { ValidationCheck } from '../domain/validation-check';
import { DuplicateGameDialogComponent } from '../duplicate-game-dialog/duplicate-game-dialog.component';
import { GameExportDialogComponent } from '../game-export-dialog/game-export-dialog.component';
import { GameQualityReportDialogComponent } from '../game-quality-report-dialog/game-quality-report-dialog.component';
import { GameQualityDialogComponent } from '../game-quality-dialog/game-quality-dialog.component';
import { GameSyncDialogComponent } from '../game-sync-dialog/game-sync-dialog.component';
import { GameValidationDialogComponent } from '../game-validation-dialog/game-validation-dialog.component';
import {
  ExportWarning,
  ExportWarningsDialogComponent
} from '../export-warnings-dialog/export-warnings-dialog.component';
import { ImportSIHFGoalsDialogComponent } from '../import-sihf-goals-dialog/import-sihf-goals-dialog.component';
import { LineupChangeDialogComponent } from '../lineup-change-dialog/lineup-change-dialog.component';
import { AlertService } from '../services/alert.service';
import { EventService } from '../services/event.service';
import { GameService } from '../services/game.service';
import { CollectionEffortComponent } from '../shared/collection-stats/collection-effort.component';
import { FileUploadDialogComponent } from '../shared/file-upload-dialog/file-upload-dialog.component';
import { PlayerSummaryComponent } from '../shared/player-summary/player-summary.component';
import { UpdateFOINTDialogComponent } from '../update-foint-dialog/update-foint-dialog.component';
import { VideoTimeOffsetDialogComponent } from '../video-time-offset-dialog/video-time-offset-dialog.component';
import { ConfirmationDialogComponent } from '../shared/confirmation-dialog/confirmation-dialog.component';
import { RemoveNoEventsDialogComponent } from '../remove-no-events-dialog/remove-no-events-dialog.component';
import { MatSnackBar } from '@angular/material/snack-bar';

type runAggregationError = 'ALREADY_IN_PROGRESS' | 'NOT_EXPORTED';

@Component({
  selector: 'app-game-action-menu',
  templateUrl: './game-action-menu.component.html',
  styleUrls: ['./game-action-menu.component.css']
})
export class GameActionMenuComponent {
  readonly gameStatus = GameStatus;

  exporting = false;

  @Input()
  game: Game;

  @Output()
  gameUpdated = new EventEmitter<void>();

  @Output()
  statusUpdated = new EventEmitter<GameStatus>();

  @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

  constructor(
    private gameService: GameService,
    public authService: AuthService,
    private eventService: EventService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private snackbar: MatSnackBar
  ) {}

  duplicate(game: Game) {
    const dialogRef = this.dialog.open(DuplicateGameDialogComponent, {
      data: { game }
    });
    dialogRef.afterClosed().subscribe((duplicateEventTypes) => {
      if (duplicateEventTypes) {
        this.gameService
          .duplicateGame(game._id, duplicateEventTypes)
          .subscribe(async (newGame: Game) => {
            this.alertService.showInfo('Game duplicated', 'View Game Setup');
            const previousRouteReuseStrategy =
              this.router.routeReuseStrategy.shouldReuseRoute;
            this.router.routeReuseStrategy.shouldReuseRoute = () => false;
            this.router.onSameUrlNavigation = 'reload';

            await this.router.navigate(['/games', newGame._id]);

            this.router.routeReuseStrategy.shouldReuseRoute =
              previousRouteReuseStrategy;
            this.router.onSameUrlNavigation = 'ignore';
          });
      }
    });
  }

  delete(game: Game) {
    if (game.aggregationStatus === AggregationStatus.IN_PROGRESS) {
      this.alertService.showError(
        'Cannot delete game while aggregation is in progress'
      );
      return;
    }
    if (
      confirm(
        `Are you sure to delete the game ${game.date.substr(0, 10)} ${
          game.homeTeam
        }-${game.awayTeam} including all events ?`
      )
    ) {
      this.gameService.delete(game).subscribe({
        next: async () => {
          this.gameUpdated.emit();
          await this.router.navigate(['/games']);
        },
        error: (err) => {
          this.alertService.showError(
            `Delete failed: ${err.error?.message || err.message}`
          );
        }
      });
    }
  }

  updateStatus(game: Game, status: GameStatus): void {
    this.gameService.updateStatus(game._id, status).subscribe(
      (result) => {
        game.status = status;
        if (result && result instanceof Array) {
          this.gameService.updateGameValidations(
            game,
            result as ValidationCheck[]
          );
        }
        this.statusUpdated.emit(status);

        if (status === GameStatus.COMPLETE) {
          if (game.isTrainingGame) {
            this.snackbar.open(`Game is not synced: it's a training game`);
            return;
          }

          this.exporting = true;
          this.gameService
            .syncGame(game._id, 'all')
            .pipe(finalize(() => (this.exporting = false)))
            .subscribe(
              async (res) => {
                console.log('Sync game succeeded');
                this.gameUpdated.emit();
                this.handleSyncResult(res);
              },
              (err) => {
                alert('Sync game failed: ' + err.error.message);
              }
            );
        }
      },
      (err) =>
        this.alertService.showError('Update status failed: ' + err.message)
    );
  }

  validateGame(game: Game): void {
    this.exporting = true;
    this.gameService
      .validateGame(game._id)
      .pipe(finalize(() => (this.exporting = false)))
      .subscribe(
        (validations) => {
          this.gameService.updateGameValidations(game, validations);
          this.dialog.open(GameValidationDialogComponent, {
            width: '400px',
            data: { game, validations }
          });
        },
        (err) => {
          alert('Validate game failed: ' + err.error.message);
        }
      );
  }

  adjustLineup(game: Game): void {
    this.dialog.open(LineupChangeDialogComponent, {
      width: '800px',
      data: { game },
      panelClass: 'lineup-dialog-container'
    });
  }

  backfillEventIds(game: Game) {
    this.exporting = true;
    this.gameService
      .backfillGameEventIds(game._id, true)
      .pipe(finalize(() => (this.exporting = false)))
      .subscribe(
        (res) => {
          this.alertService.showInfo(
            `Backfill complete: ${res.changedFields} field(s) filled ` +
              `across ${res.changedEvents} event(s). ` +
              `${res.unresolvedFields} reference(s) could not be resolved.`
          );
        },
        (err) => {
          this.alertService.showError(
            'Backfill event IDs failed: ' + (err?.error?.message || err.message)
          );
        }
      );
  }

  splitShiftsByStrengthState(game: Game) {
    this.eventService.splitShiftsByStrengthState(game._id).subscribe(
      (res) => {
        this.alertService.showInfo(res.message);
      },
      (err) => {
        this.alertService.showError(
          'Update strength states failed: ' + err.message
        );
      }
    );
  }

  applyVideoTimeOffset(game: Game) {
    const dialogRef = this.dialog.open(VideoTimeOffsetDialogComponent, {
      width: '640px',
      data: { game }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.offset && result.offset !== 0) {
        this.eventService
          .applyVideoTimeOffset(game._id, result.period, result.offset)
          .subscribe(
            (res) => {
              alert(res.message);
            },
            (err) => {
              alert('Apply videoTime offset failed: ' + err.message);
            }
          );
      }
    });
  }

  updateSIHFPeriodEvents(gameId: string) {
    this.eventService.updateSIHFPeriodEvents(gameId).subscribe({
      next: (res) => {
        this.alertService.showInfo(res.message);
      },
      error: (err) => {
        this.alertService.showError(
          'Update SIHF period events failed: ' + err.error.message
        );
      }
    });
  }

  recalculateFOINTGameTime(game: Game) {
    this.dialog
      .open(UpdateFOINTDialogComponent, {
        data: {
          gameName: this.gameService.getGameTitle(game)
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.eventService
            .recalculateFOINTGameTime(game._id, result.periods)
            .subscribe({
              next: (res) => {
                this.snackbar.open(res.message);
              },
              error: (err) => {
                this.snackbar.open(
                  'Recalculate FOINT game time failed: ' + err.message,
                  'Close'
                );
              }
            });
        }
      });
  }

  updateGameTimes(game: Game) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: `Interpolating GameTimes for ${this.gameService.getGameTitle(
            game
          )}`,
          message: `Game time will be derived from FO/INT for all events, except Face-Offs and Interruptions`,
          okButtonCaption: 'Interpolate'
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.eventService.updateGameTime(game._id).subscribe(
            (res) => {
              alert(res.message);
            },
            (err) => {
              alert('Update game times failed: ' + err.message);
            }
          );
        }
      });
  }

  updateVideoTimes(game: Game) {
    this.dialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: `Interpolating VideoTimes for ${this.gameService.getGameTitle(
            game
          )}`,
          message: `Video time will be derived from FO/INT for all events, except Face-Offs and Interruptions`,
          okButtonCaption: 'Interpolate'
        }
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.eventService.updateVideoTime(game._id).subscribe(
            (res) => {
              alert(res.message);
            },
            (err) => {
              alert('Update video times failed: ' + err.message);
            }
          );
        }
      });
  }

  updateStrengthStates(game: Game) {
    this.eventService.updateStrengthState(game._id).subscribe(
      (res) => {
        alert(res.message);
      },
      (err) => {
        alert('Update strength states failed: ' + err.message);
      }
    );
  }

  removePlayersWithoutEvents(game: Game) {
    const dialogRef = this.dialog.open(RemoveNoEventsDialogComponent, {
      width: '420px',
      data: { game }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      const teams: Array<'home' | 'away'> = [];
      if (result.teams?.home) {
        teams.push('home');
      }
      if (result.teams?.away) {
        teams.push('away');
      }
      if (teams.length === 0) {
        this.alertService.showWarning(
          'No teams selected to remove players without events'
        );
        return;
      }

      this.gameService.removePlayersWithoutEvents(game._id, teams).subscribe(
        ({ message }) => this.alertService.showInfo(message),
        (err) =>
          this.alertService.showError(
            'Remove players without events failed: ' + err.message
          )
      );
    });
  }

  finalizationComplete(game: Game) {
    game.finalization = {
      ...game.finalization,
      status: FinalizationStatus.COMPLETE
    };
    // TODO: check download status of removed videos
    game.videos = game.videos.filter(
      (v) => v.format === 'mp4' || v.url.startsWith('https://videos.49ing.ch')
    );
    this.gameService
      .update(game._id, {
        finalization: game.finalization,
        videos: game.videos
      })
      .subscribe();
  }

  downloadShiftsCSV(game: Game) {
    this.gameService.getGameShiftsAsCSV(game._id).subscribe((data) => {
      if (!data) {
        this.alertService.showWarning('No shift data available to download.');
        return;
      }
      const filename = 'shifts_' + game._id + '.csv';
      this.downloadFile(data, game, filename, 'iso-8859-1');
    });
  }

  downloadGameEventsCSV(game: Game) {
    this.gameService.getGameEventsAsCSV(game).subscribe((res) => {
      if (res) {
        const filename =
          'game_events_' +
          game.date +
          '_' +
          game.homeTeam +
          '-' +
          game.awayTeam +
          '_by_' +
          game.eventCollectorName +
          '.csv';
        this.downloadFile(res, game, filename, 'iso-8859-1');
      }
    });
  }

  uploadGameEventsCSV(game: Game) {
    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: '320px',
      height: '200px',
      data: { game }
    });
    dialogRef.afterClosed().subscribe((result) => {
      console.log('Importing Game Events', { gameId: game._id });
      this.gameService.importGameEventsAsCSV(game, result.data).subscribe(
        async () => {
          console.log('Upload complete');
          this.gameUpdated.emit();
        },
        (err) => {
          console.error('failed to upload CSV: ' + err.message);
        }
      );
    });
  }

  downloadFile(data: string, game: Game, filename: string, encoding = 'utf-8') {
    let blob;
    if (encoding === 'utf-8') {
      blob = new Blob(['\ufeff' + data], { type: 'text/csv;charset=utf-8;' });
    } else {
      blob = new Blob([data], { type: 'text/csv;charset=' + encoding + ';' });
    }
    const dwldLink = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const isSafariBrowser =
      navigator.userAgent.indexOf('Safari') !== -1 &&
      navigator.userAgent.indexOf('Chrome') === -1;
    if (isSafariBrowser) {
      // if Safari open in new window to save file with random filename.
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename);
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
  }

  sihfExportDialog(game: Game) {
    this.dialog.open(GameExportDialogComponent, {
      width: '1000px',
      data: { game }
    });
  }

  runAggregation(game: Game) {
    const gameName = `${game.date.substr(0, 10)} ${game.homeTeam}-${
      game.awayTeam
    }`;
    this.gameService.runAggregation(game._id).subscribe({
      next: (res) => {
        game.aggregationStatus = AggregationStatus.IN_PROGRESS;
        game.aggregationTaskArn = res.aggregationTaskArn;
        this.alertService.showInfo(
          `Aggregation started for game '${gameName}'`
        );
      },
      error: (err) => {
        if (err.error?.errorType) {
          switch (err.error.errorType as runAggregationError) {
            case 'ALREADY_IN_PROGRESS': {
              game.aggregationStatus = AggregationStatus.IN_PROGRESS;
              game.aggregationTaskArn = err.error.aggregationTaskArn;
              this.alertService.showWarning(
                `Aggregation is already in progress for game '${gameName}'`
              );
              break;
            }
            case 'NOT_EXPORTED': {
              this.alertService.showWarning(err.error.message);
              break;
            }
            default: {
              this.alertService.showWarning(err.error.message);
            }
          }
        } else {
          game.aggregationStatus = AggregationStatus.FAILED;
          game.aggregationTaskArn = null;
          this.alertService.showError(
            `Aggregation failed for game '${gameName}': ${
              err.error?.message || err.message
            }`
          );
        }
      }
    });
  }

  syncGame(game: Game): void {
    const dialog = this.dialog.open(GameSyncDialogComponent, {
      width: '400px',
      data: { game }
    });
    dialog.afterClosed().subscribe((period) => {
      if (!period) {
        return;
      }

      this.exporting = true;
      this.gameService
        .syncGame(game._id, period)
        .pipe(finalize(() => (this.exporting = false)))
        .subscribe(
          async (res) => {
            console.log('Sync game succeeded');
            this.gameUpdated.emit();
            this.handleSyncResult(res);
          },
          (err) => {
            alert('Sync game failed: ' + err.error.message);
          }
        );
    });
  }

  private handleSyncResult(res: {
    message?: string;
    warnings?: ExportWarning[];
  }): void {
    if (res?.warnings?.length) {
      this.dialog.open(ExportWarningsDialogComponent, {
        width: '560px',
        data: { message: res.message, warnings: res.warnings }
      });
    } else if (res?.message) {
      alert(res.message);
    }
  }

  openCvShowcaseVideoPlayer(game: Game): void {
    const url = `/games/${game._id}/cv-showcase-video-player`;
    window.open(url, '_blank');
  }

  runCVPipeline(game: Game): void {
    const dialog = this.dialog.open(CvPipelineDialogComponent, {
      width: '400px',
      data: { game }
    });
    dialog.afterClosed().subscribe(async (data) => {
      if (!data?.periods || !data?.phases) {
        return;
      }
      const { liveMode, periods, phases, priority } = data;
      try {
        await firstValueFrom(
          this.gameService.runCVPipeline(
            game._id,
            liveMode,
            periods,
            phases,
            priority
          )
        );
        game.isCVProcessed = true; // update in-memory state
        this.alertService.showInfo(
          'CV-Pipeline started for ' + game.homeTeam + ' - ' + game.awayTeam
        );
      } catch (err) {
        this.alertService.showError(err.error.message);
      }
    });
  }

  showGameCollectionQuality(game: Game): void {
    this.dialog.open(GameQualityReportDialogComponent, {
      width: '1400px',
      data: {
        game
      }
    });
  }

  showGameCollectionEffort(game: Game): void {
    this.dialog.open(CollectionEffortComponent, {
      width: '900px',
      data: {
        game
      }
    });
  }

  showTimeOnIceQualityReport(game: Game): void {
    this.gameService.timeOnIceReport(game._id).subscribe((report) => {
      this.dialog.open(GameQualityDialogComponent, {
        width: '400px',
        data: { game, report }
      });
    });
  }

  showPlayerSummary(game: Game): void {
    this.dialog.open(PlayerSummaryComponent, {
      width: '640px',
      data: {
        game
      }
    });
  }

  exportGoalClips(game: Game) {
    this.eventService.exportGoalClips(game._id).subscribe({
      next: ({ message }) => {
        this.alertService.showInfo(message);
      },
      error: (err) => {
        this.alertService.showError(
          `Export goal clips failed: ${err.error?.message || err.message}`
        );
      }
    });
  }

  exportStathletes(game: Game) {
    this.exporting = true;
    this.gameService
      .exportStathletes(game._id)
      .pipe(finalize(() => (this.exporting = false)))
      .subscribe({
        // the game is left as loaded: the job records the outcome on it
        next: () => {
          this.alertService.showInfo(
            'Queued the Stathletes export: the game is delivered within about ' +
              'a minute, its outcome is recorded on the game'
          );
        },
        error: (err) => {
          this.alertService.showError(
            `Stathletes export failed: ${err.error?.message || err.message}`
          );
        }
      });
  }

  exportVideoReviewClips(game: Game) {
    this.eventService.exportVideoReviewClips(game._id).subscribe({
      next: ({ message }) => {
        this.alertService.showInfo(message);
      },
      error: (err) => {
        this.alertService.showError(
          `Export video review clips failed: ${
            err.error?.message || err.message
          }`
        );
      }
    });
  }

  importGoalsSihfReporter(gameId: string) {
    const dialogRef = this.dialog.open<
      ImportSIHFGoalsDialogComponent,
      any,
      { scorer: boolean; assists: boolean }
    >(ImportSIHFGoalsDialogComponent, {
      data: { gameId }
    });
    dialogRef.afterClosed().subscribe((options) => {
      if (!options) {
        return;
      }
      this.gameService
        .importGoalsFromSihfReporter(gameId, options)
        .subscribe(async (res) => {
          if (res.message) {
            this.alertService.showInfo(res.message);
            return;
          }
          await this.router.navigate(['/games', gameId, 'events'], {
            queryParams: { eventType: 'shot', shotOutcome: 'goal' }
          });
        });
    });
  }

  importPenaltiesSihfReporter(gameId: string) {
    this.gameService
      .importPenaltiesFromSihfReporter(gameId)
      .subscribe(async (res) => {
        if (res.message) {
          this.alertService.showInfo(res.message);
          return;
        }

        await this.router.navigate(['/games', gameId, 'events'], {
          queryParams: { eventType: 'penalty' }
        });
      });
  }

  syncGameIncidents(gameId: string) {
    this.gameService.syncGameIncidents(gameId).subscribe(async (res) => {
      if (res.message) {
        this.alertService.showInfo(res.message);
        return;
      }

      await this.router.navigate(['/games', gameId, 'events'], {
        queryParams: { eventType: 'game_incident' }
      });
    });
  }
}
